<script setup>
import { ref } from "vue";
import ITeams from "../ITeams/ITeams.vue";
import ITicket from "../ITicket/ITicket.vue";
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import ILink from "../ILink/ILink.vue";
import ExtraInfo from "../ExtraInfo.vue";
import { dateHelper } from '@shop/frontend/web/js/helpers/dateHelper';
import useCartStore from "../../store/modules/cart";

const cartStore = useCartStore();
const props = defineProps({
	itemCase: {
		type: Object,
		required: true,
	},
	trashButton: {
		type: Boolean,
		default: true,
	},
	isHistory: {
		type: Boolean,
		default: false,
	},
});

const emits = defineEmits(["removeEvent"]);

const eventFormattedDate = ref(
	`${dateHelper.stampTodm(props.itemCase.startTimestamp)} в ${dateHelper.getFormattedDate(props.itemCase.startTimestamp, 'HH:mm')}`
);

function removeTicket(ticket, eventId, type) {
	if (type === "service_simple") {
		cartStore.removeServiceTicket(ticket, eventId);
	} else {
		cartStore.removeEventTicket(ticket, eventId);
	}
}

function removeEvent(eventId, type, countTickets) {
	if (type === "service_simple") {
		cartStore.removeService(eventId, props.itemCase.orderId, countTickets);
	} else {
		cartStore.removeEvent(eventId, props.itemCase.orderId);
	}
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
	cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}

const isPackageEventsShow = ref(false);
</script>

<template>
	<div class="i-order-event-item">
		<a class="i-order-event-item__team-link" :href="itemCase.pageLink">
			<div class="i-order-event-item__top-left">
				<template v-if="itemCase.type == 'vagrant'">
					<div class="i-order-event-item__name">
						<ITeams
							v-if="itemCase.participants?.team"
							:first-team="itemCase.participants.team.homeTeam"
							:second-team="itemCase.participants.team.guestTeam"
						/>
						<div v-else class="name">
							<div class="name__title">
								{{ itemCase.participants?.singular.name }}
							</div>
						</div>
					</div>
					<div class="i-order-event-item__details">
						<span class="time">
							<svg>
								<use xlink:href="#calendar"></use>
							</svg>
							<span class="text-lowercase">{{ eventFormattedDate }}</span>
						</span>
						<span class="place">
              <svg>
                  <use xlink:href="#location"></use>
                </svg>
							{{ itemCase.unitName }}
						</span>
					</div>
				</template>
				<template v-else>
					<div
						class="i-order-event-item__name"
						:class="{'i-order-event-item__name_service' : itemCase.type === 'service_simple'}"
					>
						<div class="name">
							<div class="name__title">{{ itemCase.name }}</div>
						</div>
					</div>
				</template>
			</div>
		</a>
		<div class="i-order-event-item__top-right">
			<div class="i-order-event-item__summary-price">
				{{ translator.t("main", "Price") }}: <span>{{ itemCase.summaryPrice }} ₽</span>
			</div>
			<ILink
				v-if="trashButton"
				:icon-name="'trash'"
				class="desktop trash-btn"
				@click="
					removeEvent(
						itemCase.id,
						type ?? itemCase.type,
						ticketsCount ?? itemCase.tickets.length
					)
				"
			>
				{{ translator.t("main", "Remove") }}
			</ILink>
		</div>
		<div class="i-order-event-item__tickets">
			<slot>
				<div v-for="ticket in itemCase.tickets" :key="ticket.place" class="tickets-item">
					<i-ticket
						:ticket="ticket"
						:is-history="isHistory"
						:inactive="false"
						:short="false"
						:allowed-loyalty="itemCase.allowedLoyaltyCategories"
						@remove-ticket="
							curTicket => removeTicket(curTicket, itemCase.id, itemCase.type)
						"
						@change-loyalty="
							({ ticket, categoryId, priceId }) =>
								changeLoyalty(ticket, itemCase.id, categoryId, priceId)
						"
					>
						<ExtraInfo
							v-if="ticket.isEnableExtraInfo"
							:key="ticket.uid"
							:ticket="ticket"
							:is-history="isHistory"
						/>
					</i-ticket>
				</div>
			</slot>
		</div>
	</div>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-order-event-item.scss";

.fade-enter-active,
.fade-leave-active {
	transition: height 0.5s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
	overflow: hidden;
	height: 100%;
}

.fade-enter-from,
.fade-leave-to {
	overflow: hidden;
	height: 0;
}
</style>
